/**
 * @file contentPayload.js
 * @description 문구 초안을 저장 형식으로 바꾸고 최신 서버 맵을 정화 병합합니다.
 * @used-in pages/admin/ContentAdminPanel.jsx
 */
import { getContentEntry, getDefaultText, getDefaultItems } from '../content/registry';
import { getBaseHash } from '../content/baseHash';

// [비즈니스] 서버의 목록 제한과 같은 UI 가드이며 CP-14가 값의 차이를 감지합니다.
export const MAX_LIST_ITEMS = 40;

// [기능] 문자열의 연속 개행을 공백 한 칸으로 바꾸고 비문자열은 빈 문자열로 처리합니다.
// [비즈니스] 개행은 입력 계층에서 제거하며 다른 연속 공백은 그대로 보존합니다.
export function stripLineBreaks(value) {
  return typeof value === 'string' ? value.replace(/[\r\n\u2028\u2029]+/g, ' ') : '';
}

// [기능] 배열과 클래스 인스턴스를 제외한 일반 객체인지 확인합니다.
function isPlainObject(value) {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// [기능] 초안에서 기본값과 다른 문구만 추려 코드 기본값의 지문과 함께 반환합니다.
export function buildContentValues(draft) {
  const result = Object.create(null);
  for (const key of Object.keys(draft.texts)) {
    const entry = getContentEntry(key);
    if (!entry || entry.type === 'list' || !Object.hasOwn(draft.texts, key)) continue;
    const value = stripLineBreaks(draft.texts[key]).trim();
    // [비즈니스] 기본값과 같으면 키를 생략해 이후 코드 기본 문구 변경도 반영되게 합니다.
    if (value === getDefaultText(key)) continue;
    // [비즈니스] 빈 텍스트는 의도적으로 지운 문구이므로 유효한 오버라이드입니다.
    const out = { v: value };
    const hash = getBaseHash(key);
    if (typeof hash === 'string') out.baseHash = hash;
    result[key] = out;
  }
  for (const key of Object.keys(draft.lists)) {
    const entry = getContentEntry(key);
    if (!entry || entry.type !== 'list' || !Object.hasOwn(draft.lists, key)) continue;
    // [비즈니스] 빈 문단은 공개 화면에 빈 줄만 남기므로 저장하지 않습니다.
    const items = draft.lists[key].map((text) => stripLineBreaks(text).trim()).filter((text) => text !== '');
    const defaults = getDefaultItems(key);
    if (items.length === defaults.length && items.every((text, index) => text === defaults[index])) continue;
    const out = { items };
    // [비즈니스] 사용자 입력값이 아닌 코드 기본값의 지문을 저장합니다.
    const hash = getBaseHash(key);
    if (typeof hash === 'string') out.baseHash = hash;
    result[key] = out;
  }
  return result;
}

// [기능] 외부 항목을 서버가 받는 형태로 복사하고 표현 불가 항목은 null을 반환합니다.
function sanitizeEntry(entry) {
  if (!isPlainObject(entry)) return null;
  if (Object.hasOwn(entry, 'v') && Object.hasOwn(entry, 'items')) return null;
  let out;
  if (Array.isArray(entry.items) && entry.items.every((item) => typeof item === 'string')) {
    out = { items: entry.items.slice() };
  } else if (typeof entry.v === 'string') {
    out = { v: entry.v };
  } else {
    return null;
  }
  // [기술] 조회 응답의 baseHash:null을 그대로 PUT하면 400이므로 필드째 제거합니다.
  if (typeof entry.baseHash === 'string') out.baseHash = entry.baseHash;
  return out;
}

// [기능] 최신 외부 문구와 패널 초안을 입력 변형 없이 병합하고 누락 위험 키를 보고합니다.
export function mergeContentValues(serverValues, ownValues, ownedKeys) {
  const owned = new Set(ownedKeys);
  const values = Object.create(null);
  const droppedKeys = [];
  if (isPlainObject(serverValues)) {
    for (const [key, entry] of Object.entries(serverValues)) {
      // [비즈니스] 소유 키가 초안에 없으면 기본값 복귀이므로 서버 값도 제거합니다.
      if (owned.has(key)) continue;
      const sanitized = sanitizeEntry(entry);
      if (sanitized === null) {
        // [비즈니스] 전체 교체에서 조용한 누락은 데이터 소실이므로 호출부가 저장을 중단합니다.
        droppedKeys.push(key);
        continue;
      }
      values[key] = sanitized;
    }
  }
  for (const [key, entry] of Object.entries(ownValues)) values[key] = entry;
  return { values, droppedKeys };
}
